import { useState, useEffect, useCallback } from 'react';
import { esocialService, type Envio, type CatListItem } from '../services/esocial.service';
import { useEmpresa } from '../contexts/EmpresaContext';
import {
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import CustomerCatWizardModal from '../components/Esocial/CustomerCatWizardModal';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';

export default function ESocial() {
  const { empresa } = useEmpresa();
  const [activeTab, setActiveTab] = useState<'envios' | 'cats'>('envios');

  // Envios State
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [isLoadingEnvios, setIsLoadingEnvios] = useState(true);
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  // CAT State
  const [cats, setCats] = useState<CatListItem[]>([]);
  const [isLoadingCats, setIsLoadingCats] = useState(false);
  const [isCatWizardOpen, setIsCatWizardOpen] = useState(false);

  const loadEnvios = useCallback(async () => {
    setIsLoadingEnvios(true);
    try {
      const data = await esocialService.listEnvios(filtroMes, filtroStatus);
      setEnvios(data);
    } catch (error) {
      console.error('❌ Erro ao carregar envios:', error);
    } finally {
      setIsLoadingEnvios(false);
    }
  }, [filtroMes, filtroStatus]);

  const loadCats = useCallback(async () => {
    setIsLoadingCats(true);
    try {
      const data = await esocialService.listCats();
      setCats(data);
    } catch (error) {
      console.error('❌ Erro ao carregar CATs:', error);
    } finally {
      setIsLoadingCats(false);
    }
  }, []);

  useEffect(() => {
    if (empresa?.id) {
      loadEnvios();
      loadCats();
    }
  }, [empresa?.id, loadEnvios, loadCats]);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'enviado':
      case 'processado':
      case 'accepted':
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
      case 'processando':
      case 'enviando':
      case 'queued':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'erro':
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pendente':
      case 'draft':
      case 'pending_validation':
        return <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />;
      case 'auto_healed':
        return <SparklesIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pendente: 'Pendente',
      processando: 'Processando',
      enviado: 'Enviado',
      erro: 'Erro',
      cancelado: 'Cancelado',
      DRAFT: 'Rascunho',
      PENDING_VALIDATION: 'Pendente de Validação',
      VALIDATED: 'Validado',
      QUEUED: 'Na Fila de Transmissão',
      SENT: 'Enviado ao Governo',
      ACCEPTED: 'Aceito / Processado',
      REJECTED: 'Rejeitado pelo eSocial',
      AUTO_HEALED: 'Auto-Curado / Resolvido',
    };
    return labels[status] || status;
  };

  const getTipoAcidenteLabel = (tipo: string) => {
    switch (tipo) {
      case '1':
        return 'Típico';
      case '2':
        return 'Trajeto';
      case '3':
        return 'Doença Ocupacional';
      default:
        return 'Outro';
    }
  };

  const handleDownloadRelatorio = async (relatorioId: string) => {
    try {
      await esocialService.downloadRelatorio(relatorioId);
    } catch (error) {
      console.error('❌ Erro ao baixar relatório:', error);
      alert('Erro ao baixar relatório');
    }
  };

  // Gerar lista de meses para o filtro (últimos 12 meses)
  const meses = [];
  const hoje = new Date();
  for (let i = 0; i < 12; i++) {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const mes = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const label = data.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' });
    meses.push({ value: mes, label });
  }

  return (
    <PageContainer>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>eSocial SST</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
              S-2210 / S-2220 / S-2240
            </span>
          </div>
        }
        description="Controle de transmissões de eventos de Saúde e Segurança do Trabalho para o Governo Federal"
        actions={
          <button
            onClick={() => setIsCatWizardOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-white bg-green-600 hover:bg-green-500 shadow-sm transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Emitir Nova CAT (S-2210)
          </button>
        }
      />

        {/* Tab Switcher Moderno */}
        <div className="flex border-b border-gray-200 mb-6 gap-8">
          <button
            onClick={() => setActiveTab('envios')}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'envios'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Lotes e Relatórios Mensais
          </button>
          <button
            onClick={() => setActiveTab('cats')}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'cats'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Comunicações de Acidente - CAT (S-2210)
            {cats.length > 0 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-800 font-bold">
                {cats.length}
              </span>
            )}
          </button>
        </div>

        {/* ABA 1: ENVIOS MENSAIS */}
        {activeTab === 'envios' && (
          <div>
            {/* Filtros */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Mês de Referência
                  </label>
                  <select
                    value={filtroMes}
                    onChange={(e) => setFiltroMes(e.target.value)}
                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm py-2"
                  >
                    <option value="">Todos os meses</option>
                    {meses.map((mes) => (
                      <option key={mes.value} value={mes.value}>
                        {mes.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Status
                  </label>
                  <select
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value)}
                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm py-2"
                  >
                    <option value="">Todos os status</option>
                    <option value="pendente">Pendente</option>
                    <option value="processando">Processando</option>
                    <option value="enviado">Enviado</option>
                    <option value="erro">Erro</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lista de Envios */}
            {isLoadingEnvios ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto"></div>
                <p className="mt-3 text-sm text-gray-500">Carregando lotes do eSocial...</p>
              </div>
            ) : envios.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                <ShieldCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Nenhum envio mensal encontrado</p>
                <p className="text-xs text-gray-400 mt-1">
                  Os eventos gerados pelos exames clínicos são agrupados automaticamente todo mês.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {envios.map((envio) => (
                  <div key={envio.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-all hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getStatusIcon(envio.status)}
                          <h3 className="text-lg font-bold text-gray-900 capitalize">
                            {new Date(envio.mes_referencia).toLocaleDateString('pt-BR', {
                              year: 'numeric',
                              month: 'long',
                            })}
                          </h3>
                          <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            {getStatusLabel(envio.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                            <span className="text-xs text-gray-500 block">Total de Itens</span>
                            <span className="text-base font-bold text-gray-900">{envio.total_itens}</span>
                          </div>
                          <div className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-100">
                            <span className="text-xs text-emerald-700 block">Processados</span>
                            <span className="text-base font-bold text-emerald-700">
                              {envio.itens_processados}
                            </span>
                          </div>
                          <div className="bg-red-50/50 p-3 rounded-xl border border-red-100">
                            <span className="text-xs text-red-700 block">Com Erro</span>
                            <span className="text-base font-bold text-red-700">
                              {envio.itens_com_erro}
                            </span>
                          </div>
                          {envio.protocolo_envio && (
                            <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                              <span className="text-xs text-blue-700 block">Protocolo</span>
                              <span className="text-xs font-mono font-bold text-blue-900 truncate block">
                                {envio.protocolo_envio}
                              </span>
                            </div>
                          )}
                        </div>
                        {envio.data_envio && (
                          <p className="text-xs text-gray-400 mt-3">
                            Enviado em: {new Date(envio.data_envio).toLocaleString('pt-BR')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Relatórios */}
                    {envio.relatorios && envio.relatorios.length > 0 && (
                      <div className="mt-5 pt-4 border-t border-gray-100">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                          Relatórios Comprobatórios de Transmissão
                        </h4>
                        <div className="space-y-2">
                          {envio.relatorios.map((relatorio) => (
                            <div
                              key={relatorio.id}
                              className="flex items-center justify-between p-3 bg-gray-50/80 rounded-xl border border-gray-100"
                            >
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {relatorio.arquivo_nome}
                                </p>
                                <p className="text-xs text-gray-400">
                                  Gerado em {new Date(relatorio.created_at).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                              <button
                                onClick={() => handleDownloadRelatorio(relatorio.id)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100/80 rounded-lg transition-colors"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                                Baixar PDF
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ABA 2: COMUNICAÇÕES DE ACIDENTE (S-2210) */}
        {activeTab === 'cats' && (
          <div>
            {isLoadingCats ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto"></div>
                <p className="mt-3 text-sm text-gray-500">Carregando acidentes cadastrados...</p>
              </div>
            ) : cats.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="p-4 bg-emerald-50 rounded-2xl w-16 h-16 mx-auto flex items-center justify-center mb-4">
                  <ShieldCheckIcon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Nenhuma CAT emitida até o momento</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mt-1 mb-6">
                  Quando ocorrer qualquer acidente de trabalho ou diagnóstico de doença ocupacional, emita a CAT diretamente por aqui para envio automático ao eSocial.
                </p>
                <button
                  onClick={() => setIsCatWizardOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-700 shadow-md transition-all"
                >
                  <PlusIcon className="w-4 h-4 stroke-[2.5]" />
                  Emitir Primeira CAT
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50/80 border-b border-gray-200 text-xs font-bold text-gray-600 uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4">Colaborador</th>
                        <th className="px-6 py-4">Tipo & Data</th>
                        <th className="px-6 py-4">CID-10</th>
                        <th className="px-6 py-4">Status no eSocial</th>
                        <th className="px-6 py-4">Protocolo / Recibo</th>
                        <th className="px-6 py-4 text-right">Data de Cadastro</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cats.map((cat) => (
                        <tr key={cat.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-semibold text-gray-900">
                              {cat.colaborador?.nome || 'Colaborador não identificado'}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              CPF: {cat.colaborador?.cpf || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                              {getTipoAcidenteLabel(cat.tipo_acidente)}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {cat.data_acidente ? new Date(cat.data_acidente).toLocaleDateString('pt-BR') : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-mono text-xs font-bold bg-amber-50 text-amber-800 px-2 py-0.5 rounded border border-amber-200">
                              {cat.cid_10 || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              {getStatusIcon(cat.estado_atual || cat.status)}
                              <span className="text-xs font-semibold text-gray-800">
                                {getStatusLabel(cat.estado_atual || cat.status)}
                              </span>
                            </div>
                            {cat.mensagem_erro && (
                              <p className="text-[11px] text-red-600 mt-0.5 max-w-xs truncate" title={cat.mensagem_erro}>
                                {cat.mensagem_erro}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {cat.recibo_processamento ? (
                              <span className="text-xs font-mono font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                Recibo: {cat.recibo_processamento}
                              </span>
                            ) : cat.protocolo_envio ? (
                              <span className="text-xs font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                Prot: {cat.protocolo_envio}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 italic">Aguardando lote</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right text-xs text-gray-500">
                            {new Date(cat.created_at).toLocaleDateString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal de Emissão de CAT */}
        {empresa?.id && (
          <CustomerCatWizardModal
            isOpen={isCatWizardOpen}
            onClose={() => setIsCatWizardOpen(false)}
            empresaId={Number(empresa.id)}
            onSuccess={() => {
              loadCats();
              setActiveTab('cats');
            }}
          />
        )}
    </PageContainer>
  );
}
